// src/pages/Tickets.js
import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, Plus, Edit2, Trash2, RefreshCw, MessageSquare, AlertTriangle, Clock, CheckCircle, X, Save, Send, Search, User } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const PRIORITY_CFG = {
  low:      { label: 'Scăzut',   color: '#6b7280', bg: '#6b728020' },
  medium:   { label: 'Mediu',    color: 'var(--accent)', bg: '#4f8ef720' },
  high:     { label: 'Ridicat',  color: 'var(--warning)', bg: '#f59e0b20' },
  critical: { label: 'Critic',   color: 'var(--danger)', bg: '#ef444420' },
};
const STATUS_CFG = {
  open:        { label: 'Deschis',    color: 'var(--accent)' },
  in_progress: { label: 'În lucru',   color: 'var(--warning)' },
  waiting:     { label: 'Așteptare',  color: '#8b5cf6' },
  resolved:    { label: 'Rezolvat',   color: 'var(--success)' },
  closed:      { label: 'Închis',     color: 'var(--text-secondary)' },
};

function PriorityBadge({ priority }) {
  const c = PRIORITY_CFG[priority] || PRIORITY_CFG.medium;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, color: c.color, background: c.bg }}>{c.label}</span>;
}

function TicketModal({ ticket, clients, users, onClose, onSaved }) {
  const isEdit = !!ticket?.id;
  const [f, setF] = useState(isEdit ? { ...ticket } : { title: '', description: '', priority: 'medium', category: 'general', client_id: '', assigned_to: '', due_date: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title) return toast.error('Titlul este obligatoriu');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/tickets/${ticket.id}`, f); toast.success('Ticket actualizat'); }
      else { await api.post('/tickets', f); toast.success('Ticket creat'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title"><Ticket size={16} /> {isEdit ? ticket.number : 'Ticket Nou'}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Titlu *</label><input className="form-input" value={f.title} onChange={e => set('title', e.target.value)} /></div>
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Prioritate</label>
              <select className="form-select" value={f.priority} onChange={e => set('priority', e.target.value)}>
                {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Categorie</label>
              <select className="form-select" value={f.category} onChange={e => set('category', e.target.value)}>
                {['general','technical','billing','hosting','other'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Client</label>
              <select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Fără client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Atribuit</label>
              <select className="form-select" value={f.assigned_to || ''} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">— Neatribuit —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Termen limită</label><input className="form-input" type="date" value={f.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
            {isEdit && <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>}
          </div>
          <div className="form-group"><label className="form-label">Descriere</label><textarea className="form-textarea" style={{ minHeight: 120 }} value={f.description || ''} onChange={e => set('description', e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button></div>
      </div>
    </div>
  );
}

function TicketDetailModal({ ticketId, clients, users, onClose, onUpdated }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const toast = useToast();

  const loadTicket = async () => {
    try { const { data } = await api.get(`/tickets/${ticketId}`); setTicket(data); }
    catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTicket(); }, [ticketId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${ticketId}/replies`, { message: reply, is_internal: isInternal });
      setReply(''); toast.success('Răspuns trimis'); loadTicket(); onUpdated();
    } catch (e) { toast.error('Eroare la trimitere'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="modal-overlay"><div className="modal" style={{ maxWidth: 700 }}><div className="loading-center"><span className="spinner" /></div></div></div>;
  if (!ticket) return null;

  const pCfg = PRIORITY_CFG[ticket.priority] || PRIORITY_CFG.medium;
  const sCfg = STATUS_CFG[ticket.status] || STATUS_CFG.open;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720, width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <span className="modal-title"><Ticket size={16} /> {ticket.number} — {ticket.title}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setEditMode(true)}><Edit2 size={13} /></button>
            <button className="btn-icon" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {/* Metadata */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <PriorityBadge priority={ticket.priority} />
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, color: sCfg.color, background: `${sCfg.color}20` }}>{sCfg.label}</span>
            {ticket.company_name && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>🏢 {ticket.company_name}</span>}
            {ticket.assigned_name && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>👤 {ticket.assigned_name}</span>}
            {ticket.due_date && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>📅 {new Date(ticket.due_date).toLocaleDateString('ro-RO')}</span>}
          </div>
          {ticket.description && <div style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>{ticket.description}</div>}

          {/* Replies */}
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Conversație ({ticket.replies?.length || 0} mesaje)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(ticket.replies || []).map(r => (
              <div key={r.id} style={{ background: r.is_internal ? '#f59e0b10' : 'var(--bg-hover)', border: `1px solid ${r.is_internal ? '#f59e0b40' : 'var(--border)'}`, borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{r.full_name || 'Sistem'} {r.is_internal && <span style={{ fontSize: 10, color: '#f59e0b', background: '#f59e0b20', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>Intern</span>}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{new Date(r.created_at).toLocaleString('ro-RO')}</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{r.message}</div>
              </div>
            ))}
          </div>

          {/* Reply box */}
          <div style={{ marginTop: 16 }}>
            <textarea className="form-textarea" style={{ minHeight: 80 }} placeholder="Scrie un răspuns..." value={reply} onChange={e => setReply(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} /> Notă internă
              </label>
              <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={sendReply} disabled={sending || !reply.trim()}>
                {sending ? <span className="spinner" /> : <><Send size={13} /> Trimite</>}
              </button>
            </div>
          </div>
        </div>
      </div>
      {editMode && <TicketModal ticket={ticket} clients={clients} users={users} onClose={() => setEditMode(false)} onSaved={() => { setEditMode(false); loadTicket(); onUpdated(); }} />}
    </div>
  );
}

export default function Tickets() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [modal, setModal] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, uRes, sRes] = await Promise.all([
        api.get('/tickets', { params: { search: search || undefined, status: filterStatus || undefined, priority: filterPriority || undefined } }),
        api.get('/clients'),
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/tickets/stats'),
      ]);
      setList(tRes.data); setClients(cRes.data); setUsers(uRes.data); setStats(sRes.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterPriority]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, num) => {
    if (!window.confirm(`Ștergi ticket-ul ${num}?`)) return;
    try { await api.delete(`/tickets/${id}`); toast.success('Ticket șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Ticketing & Suport</div><div className="page-subtitle">Gestionare cereri și comunicare cu clienții</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Ticket Nou</button>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total', value: stats.total, color: 'var(--accent)', icon: Ticket },
            { label: 'Deschise', value: stats.open, color: 'var(--accent)', icon: MessageSquare },
            { label: 'În lucru', value: stats.in_progress, color: 'var(--warning)', icon: Clock },
            { label: 'Rezolvate', value: stats.resolved, color: 'var(--success)', icon: CheckCircle },
            { label: 'Critice', value: stats.critical, color: 'var(--danger)', icon: AlertTriangle },
            { label: 'Prioritate Ridicată', value: stats.high, color: 'var(--warning)', icon: AlertTriangle },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}><k.icon size={16} color={k.color} /></div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută ticket..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Toate statusurile</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="form-select" style={{ width: 140 }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">Toate prioritățile</option>
            {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>NR.</th><th>TITLU</th><th>CLIENT</th><th>PRIORITATE</th><th>STATUS</th><th>ATRIBUIT</th><th>MESAJE</th><th>CREAT</th><th>ACȚIUNI</th></tr></thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Niciun ticket</td></tr>}
                {list.map(t => {
                  const sCfg = STATUS_CFG[t.status] || STATUS_CFG.open;
                  return (
                    <tr key={t.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.number}</td>
                      <td>
                        <button onClick={() => setDetailId(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, textAlign: 'left', padding: 0 }}>{t.title}</button>
                        {t.category !== 'general' && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t.category}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.company_name || t.contact_name || '—'}</td>
                      <td><PriorityBadge priority={t.priority} /></td>
                      <td><span style={{ fontSize: 11, fontWeight: 600, color: sCfg.color }}>{sCfg.label}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.assigned_name || '—'}</td>
                      <td style={{ textAlign: 'center' }}><span style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: '2px 8px', fontSize: 12 }}>{t.reply_count}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(t.created_at).toLocaleDateString('ro-RO')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" onClick={() => setDetailId(t.id)}><MessageSquare size={13} /></button>
                          {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(t.id, t.number)}><Trash2 size={13} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <TicketModal ticket={modal.new ? null : modal} clients={clients} users={users} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {detailId && <TicketDetailModal ticketId={detailId} clients={clients} users={users} onClose={() => setDetailId(null)} onUpdated={load} />}
    </div>
  );
}
